import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MIN_SEARCH_LEN, useSearch } from "@/hooks/useSearch";
import { getStickyActiveModule, setActiveModule } from "@/lib/module-context";
import { recordModuleFor } from "@/lib/modules";
import { clientPath } from "@/lib/record-paths";
import { useAuthSession } from "@/lib/session-context";
import type { MarketSearchHit } from "@/pages/market-intelligence/market-search";
import { useMarketSearch } from "@/pages/market-intelligence/useMarketSearch";
import { useReportSearch } from "@/pages/reports/useReportSearch";
import type { CustomReportSummary } from "@/types/reports";
import type { SearchResult } from "@/types/search";

const DEBOUNCE_MS = 250;

/**
 * The state behind every global-search box: the debounced term, the server's
 * records, the Market Intelligence hits, and where picking each one goes.
 *
 * Shared by the top bar (`GlobalSearch`) and the dashboard's grand search
 * (`GrandSearch`), so the two boxes can differ in size and nothing else — the
 * same term finds the same things and lands in the same place from either.
 *
 * `onNavigated` runs after a pick, for the caller to close its own popover.
 */
export function useGlobalSearch(onNavigated?: () => void) {
  const [raw, setRaw] = useState("");
  const [debounced, setDebounced] = useState("");
  const navigate = useNavigate();
  const session = useAuthSession();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [raw]);

  const { data, isFetching, isPlaceholderData } = useSearch(debounced);
  const results: SearchResult[] = data ?? [];
  const ready = debounced.trim().length >= MIN_SEARCH_LEN;
  const { hits: marketHits, enabled: marketEnabled } = useMarketSearch(debounced, ready);
  const reportHits = useReportSearch(debounced, ready);
  const settledEmpty =
    !isFetching && !isPlaceholderData && data !== undefined && results.length === 0;

  function clear() {
    setRaw("");
    setDebounced("");
  }

  function go(path: string) {
    navigate(path);
    clear();
    onNavigated?.();
  }

  function selectRecord(r: SearchResult) {
    // A record opens in the active module, so picking one from Market
    // Intelligence, Reports or Admin first moves the user to Energy Advisor
    // (or Operations), where records are worked (AB#8790). Market and report
    // hits need nothing: their routes carry their own module.
    setActiveModule(
      recordModuleFor(getStickyActiveModule(), session.permissions, session.is_super_admin),
    );
    // Contacts and accounts have no page of their own — they deep-link to
    // their customer (accounts land on the customer's Accounts tab).
    go(
      r.kind === "deal"
        ? `/deals/${r.id}`
        : r.kind === "account"
          ? `${clientPath(r.id)}/accounts`
          : clientPath(r.id),
    );
  }

  return {
    raw,
    setRaw,
    clear,
    /** The term the results describe (trimmed), for empty-state copy. */
    query: debounced.trim(),
    ready,
    results,
    marketHits,
    marketEnabled,
    reportHits,
    settledEmpty,
    selectRecord,
    selectMarket: (hit: MarketSearchHit) => go(hit.path),
    selectReport: (report: CustomReportSummary) => go(`/reports/custom/${report.id}`),
  };
}
